import { useQuery } from "@tanstack/react-query";
import { ErrorMessage, Field, useField } from "formik";
import { useTranslations } from "next-intl";
import { useState } from "react";

import useSelection from "../lib/Selection/useSelection";
import { useConfig } from "../lib/useConfig";
import useToken from "../lib/useToken";

interface GiftCard {
  giftCertificates: {
    giftCertificate: string;
    name: string;
    type: "anyAmount" | "predeterminedAmount";
    amount?: {
      amount: number;
      currency: string;
    };
  }[];
}

const GiftCardOptions = () => {
  const [focused, setFocused] = useState(false);
  const { config } = useConfig();
  const { token, setToken } = useToken();
  const { data: selection } = useSelection();
  const [field, fieldState, fieldUpdate] = useField("gift-card");
  const [, , type] = useField("type");
  const [amountField] = useField("amount");
  const t = useTranslations();

  const { data, isLoading } = useQuery<GiftCard>(
    [
      "giftCardOptions",
      config?.centra?.url,
      selection?.location.country,
      config?.centra.country,
    ],
    () => {
      return fetch(config?.centra?.url + "/gift-certificates", {
        headers: { "API-Token": token },
      })
        .then((res) => res.json())
        .then((res) => {
          return {
            ...res,
            giftCertificates: res.giftCertificates.sort((a: any, b: any) => {
              if (a.type === "anyAmount") {
                return 1;
              }
              if (b.type === "anyAmount") {
                return -1;
              }
              return a?.amount?.amountAsNumber - b?.amount?.amountAsNumber;
            }),
          };
        })
        .then((res) => {
          setToken(res.token);

          if (
            fieldState.initialValue === undefined ||
            fieldState.initialValue === ""
          ) {
            fieldUpdate.setValue(res.giftCertificates[0].giftCertificate);
            type.setValue(res.giftCertificates[0].type);
          }
          return res;
        });
    }
  );

  if (isLoading)
    return (
      <>
        <div className="skeleton giftcard-skeleton giftcard-skeleton-radio" />
        <div className="skeleton giftcard-skeleton giftcard-skeleton-radio" />
        <div className="skeleton giftcard-skeleton giftcard-skeleton-radio" />
        <div className="skeleton giftcard-skeleton giftcard-skeleton-radio" />
        <div className="skeleton giftcard-skeleton giftcard-skeleton-radio" />
      </>
    );

  return (
    <>
      {data?.giftCertificates?.map((giftCard, i) => {
        return giftCard.type === "predeterminedAmount" ? (
          <label
            key={giftCard.giftCertificate + i}
            className={
              field.value === giftCard.giftCertificate ? "selected" : ""
            }
          >
            <Field
              type="radio"
              name="gift-card"
              onChange={(e: any) => {
                type.setValue("predeterminedAmount");
                field.onChange(e);
              }}
              value={giftCard.giftCertificate}
            />
            {giftCard.amount?.amount}
            {giftCard.amount?.currency}
          </label>
        ) : (
          <>
            <label
              onMouseEnter={() => setFocused(true)}
              onMouseLeave={() => setFocused(false)}
              key={giftCard.giftCertificate + i}
              className={
                field.value === giftCard.giftCertificate
                  ? "selected anyAmountLabel"
                  : "anyAmountLabel"
              }
            >
              <Field
                type="radio"
                name="gift-card"
                onChange={(e: any) => {
                  type.setValue("anyAmount");
                  field.onChange(e);
                }}
                value={giftCard.giftCertificate}
              />
              {field.value !== giftCard.giftCertificate && t("giftcardCustom")}

              {field.value === giftCard.giftCertificate && (
                <>
                  <Field
                    style={
                      field.value === giftCard.giftCertificate && focused
                        ? { display: "block" }
                        : { display: "none" }
                    }
                    className="anyAmount"
                    min="100"
                    max="100000"
                    type="number"
                    name="amount"
                    placeholder={config?.configFields?.amount?.label}
                    disabled={field.value !== giftCard.giftCertificate}
                  />

                  {!focused && <p>{amountField.value}</p>}
                  <>
                    <span>{selection?.selection?.currency}</span>
                  </>
                </>
              )}
            </label>
            <ErrorMessage name="amount" />
          </>
        );
      })}
    </>
  );
};

export default GiftCardOptions;
